import { SessionBillingPeriod } from "@joonapp/web-shared"
import { useQuery } from "@tanstack/react-query"

import {
  getSessionBillingPeriods,
  getSessionBooking,
  getSessionBookings,
  getUpcomingSessions,
} from "./api"
import useCurrentClient from "../../pages/clients/hooks/useCurrentClient"
import { QueryKey } from "../../types/queries"
import { useCoachingGroupsQuery } from "../coachingGroups/queries"

export const useSessionBillingPeriodsQuery = () => {
  const { currentCoachingGroup } = useCurrentClient()

  return useQuery({
    queryKey: [QueryKey.SESSION_BILLING_PERIODS, currentCoachingGroup?.id],
    queryFn: () => getSessionBillingPeriods(currentCoachingGroup?.id!),
    enabled: !!currentCoachingGroup,
  })
}

export const useGetAllBillingPeriodsQuery = () => {
  const { data: coachingGroups } = useCoachingGroupsQuery()
  const coachingGroupIds = coachingGroups?.map((group) => group.id)

  const getAllSubscriptions = async () => {
    if (!coachingGroupIds) return {}

    const results = await Promise.allSettled(
      coachingGroupIds.map((groupId) => getSessionBillingPeriods(groupId))
    )

    const billingPeriodsByCoach = {} as {
      [key: number]: SessionBillingPeriod[] | null
    }
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        billingPeriodsByCoach[coachingGroupIds[index]] = result.value
      } else {
        // Handle the error case, e.g., log the error or set a default value
        console.error(
          `Failed to fetch billing periods for group ${coachingGroupIds[index]}:`,
          result.reason
        )
        billingPeriodsByCoach[coachingGroupIds[index]] = null
      }
    })

    return billingPeriodsByCoach
  }

  return useQuery({
    queryKey: [QueryKey.ALL_BILLING_PERIODS],
    queryFn: getAllSubscriptions,
    enabled: !!coachingGroups?.length,
  })
}

export const useSessionBookingsQuery = (coachingGroupId?: number) => {
  const { currentCoachingGroup } = useCurrentClient()
  const groupId = coachingGroupId || currentCoachingGroup?.id

  return useQuery({
    queryKey: [QueryKey.SESSION_BOOKINGS, groupId],
    queryFn: () => getSessionBookings(groupId!),
    enabled: !!groupId,
  })
}

export const useSessionBookingQuery = (sessionId: number) => {
  return useQuery({
    queryKey: [QueryKey.SESSION_BOOKING, sessionId],
    queryFn: () => getSessionBooking(sessionId),
    enabled: !!sessionId,
  })
}

export const useUpcomingSessionsQuery = () => {
  return useQuery({
    queryKey: [QueryKey.UPCOMING_SESSIONS],
    queryFn: () => getUpcomingSessions(),
  })
}
